// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Alert, Button, Popconfirm, Space, Tag, Typography } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import { Homeserver } from "../api";
import type { Session } from "../types";

const hs = new Homeserver();

// RecoveryCodesPanel manages one-time account-recovery codes — the fallback for a
// forgotten password when the user has neither a passkey nor a live session. Codes
// are shown ONCE on generation; only their hashes are stored server-side.
export function RecoveryCodesPanel({ session }: { readonly session: Session }) {
  const { message } = App.useApp();
  const [status, setStatus] = useState<{ total: number; remaining: number } | null>(null);
  const [codes, setCodes] = useState<string[]>([]); // freshly generated, shown once
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setStatus(await hs.recoveryStatus(session.access_token));
    } catch {
      setStatus(null);
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.access_token]);

  async function generate() {
    setBusy(true);
    try {
      const { codes: fresh } = await hs.generateRecoveryCodes(session.access_token);
      setCodes(fresh);
      await refresh();
    } catch (e) {
      message.error(`Could not generate codes: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      message.success("Recovery codes copied");
    } catch {
      message.error("Copy failed — select and copy manually");
    }
  }

  function download() {
    const blob = new Blob([`Zalmoxis recovery codes for ${session.user_id}\nKeep these safe — each works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "zalmoxis-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Recovery codes let you reset your password if you forget it and have no passkey or signed-in
        device. Each code works once — store them somewhere safe (a password manager). Generating a new
        set invalidates the old one.
      </Typography.Paragraph>

      <Typography.Text>
        Status:{" "}
        {status === null ? (
          <Tag>checking…</Tag>
        ) : status.total === 0 ? (
          <Tag>not set up</Tag>
        ) : (
          <Tag color={status.remaining === 0 ? "red" : "green"}>
            {status.remaining} of {status.total} remaining
          </Tag>
        )}
      </Typography.Text>

      <Popconfirm
        title={status && status.total > 0 ? "Replace your recovery codes?" : "Generate recovery codes?"}
        description={status && status.total > 0 ? "Your existing codes will stop working." : undefined}
        okText="Generate"
        onConfirm={generate}
      >
        <Button type="primary" loading={busy} className="recovery-generate">
          {status && status.total > 0 ? "Regenerate codes" : "Generate recovery codes"}
        </Button>
      </Popconfirm>

      {codes.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Save these now — they won't be shown again"
          description={
            <Space direction="vertical" style={{ width: "100%" }}>
              <div className="recovery-code-grid" style={{ fontFamily: "monospace", columns: 2, lineHeight: 1.7 }}>
                {codes.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
              <Space>
                <Button size="small" icon={<CopyOutlined />} onClick={copyAll}>
                  Copy all
                </Button>
                <Button size="small" icon={<DownloadOutlined />} onClick={download}>
                  Download
                </Button>
              </Space>
            </Space>
          }
        />
      )}
    </Space>
  );
}
